import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  collectionMethodsService,
  CreateCollectionMethodInput,
  UpdateCollectionMethodInput,
} from "../../services/admin/collectionMethods.service";

export function useCollectionMethodsAdmin() {
  return useQuery({
    queryKey: ["admin", "collection_methods"],
    queryFn: collectionMethodsService.getAll,
  });
}

export function useCollectionMethod(id?: string) {
  return useQuery({
    queryKey: ["admin", "collection_methods", id],
    queryFn: () => collectionMethodsService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateCollectionMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCollectionMethodInput) =>
      collectionMethodsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "collection_methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["collection_methods"] });
      notifications.show({
        title: "Success",
        message: "Collection method created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create collection method",
        color: "red",
      });
    },
  });
}

export function useUpdateCollectionMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateCollectionMethodInput) =>
      collectionMethodsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "collection_methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["collection_methods"] });
      notifications.show({
        title: "Success",
        message: "Collection method updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update collection method",
        color: "red",
      });
    },
  });
}

export function useToggleCollectionMethodActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      collectionMethodsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "collection_methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["collection_methods"] });
      notifications.show({
        title: "Success",
        message: "Collection method status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update collection method status",
        color: "red",
      });
    },
  });
}

export function useDeleteCollectionMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => collectionMethodsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "collection_methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["collection_methods"] });
      notifications.show({
        title: "Success",
        message: "Collection method deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete collection method",
        color: "red",
      });
    },
  });
}
